import type { ReaderAiMessage, ReaderAiThreadSummary } from "@/types/agentic-chat";

const RECENT_DIRECT_TURN_LIMIT = 10;
const MAX_SUMMARY_LENGTH = 900;
const MAX_TOPIC_LABEL_LENGTH = 72;
const STOPWORDS = new Set([
  "the",
  "this",
  "that",
  "with",
  "from",
  "into",
  "your",
  "about",
  "what",
  "when",
  "where",
  "which",
  "have",
  "will",
  "would",
  "should",
  "could",
  "they",
  "them",
  "then",
  "than",
  "because",
  "while",
  "there",
  "their",
  "need",
  "want",
  "help",
  "please",
  "page",
  "chat",
  "study",
]);

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

const truncate = (value: string, max: number) =>
  value.length > max ? `${value.slice(0, Math.max(0, max - 1)).trimEnd()}…` : value;

export const readerAiMessageToText = (message: ReaderAiMessage) =>
  message.blocks
    .map((block) => {
      switch (block.type) {
        case "markdown":
          return block.markdown;
        case "explanation":
          return block.text;
        case "feedback":
          return block.text;
        case "summary":
          return block.items.join("; ");
        case "plan_overview":
          return [block.title, block.goalSummary, block.horizonLabel, block.targetDate ?? ""].join(" ");
        case "plan_milestones":
          return block.items.map((item) => [item.title, item.summary ?? "", item.targetDate ?? ""].join(" ")).join(" ");
        case "daily_plan":
          return block.items.map((item) => [item.text, item.resourceTitle ?? ""].join(" ")).join(" ");
        case "plan_checkin":
          return [block.title ?? "", block.summary, block.nextAction ?? ""].join(" ");
        case "comparison":
          return [block.title ?? "", block.leftLabel, block.rightLabel, ...block.rows.map((row) => `${row.topic} ${row.left} ${row.right}`)].join(" ");
        case "worked_example":
          return [block.title ?? "", ...block.steps, block.takeaway ?? ""].join(" ");
        case "math":
          return [block.latex, block.explanation ?? ""].join(" ");
        case "formula_sheet":
          return [block.title ?? "", ...block.items.map((item) => `${item.label} ${item.latex ?? item.text ?? ""}`)].join(" ");
        case "translation_compare":
          return block.translated;
        case "mcq":
          return block.prompt;
        case "essay_prompt":
          return block.prompt;
        case "essay_workspace":
          return block.prompt;
        case "essay_evaluation":
          return [block.highlights, block.missing].join(" ");
        case "note":
          return `${block.title} ${block.content}`;
        case "table":
          return [block.columns.join(" "), ...block.rows.map((row) => row.join(" "))].join(" ");
        case "checklist":
          return [block.title ?? "", ...block.items.map((item) => item.text)].join(" ");
        case "reflection":
          return block.prompt;
        case "source":
          return block.source.text;
        default:
          return "";
      }
    })
    .join(" ");

export const getRecentConversationTurns = (messages: ReaderAiMessage[], turnLimit = RECENT_DIRECT_TURN_LIMIT): ReaderAiMessage[] => {
  const conversational = messages.filter((message) => message.role !== "system");
  return conversational.slice(-turnLimit * 2);
};

export const summarizeThreadMessages = (
  threadId: string,
  messages: ReaderAiMessage[],
  previous?: ReaderAiThreadSummary | null,
): ReaderAiThreadSummary => {
  const conversational = messages.filter((message) => message.role !== "system");
  const older = conversational.slice(0, Math.max(0, conversational.length - RECENT_DIRECT_TURN_LIMIT * 2));
  const summaryLines = older
    .slice(-8)
    .map((message) => `${message.role === "user" ? "Student" : "Toofi"}: ${truncate(normalizeWhitespace(readerAiMessageToText(message)), 140)}`)
    .filter((line) => line.length > 12);
  const rollingSummary = truncate(summaryLines.join("\n"), MAX_SUMMARY_LENGTH);

  const userPrompts = conversational
    .filter((message) => message.role === "user")
    .map((message) => normalizeWhitespace(readerAiMessageToText(message)))
    .filter(Boolean);

  const activeGoals = userPrompts.slice(-3).map((prompt) => truncate(prompt, 120));
  const assistantQuestions = conversational
    .filter((message) => message.role === "assistant")
    .flatMap((message) =>
      readerAiMessageToText(message)
        .split(/(?<=[?.!؟])\s+/)
        .map((part) => normalizeWhitespace(part))
        .filter((part) => /[?؟]$/.test(part) || /review|quiz|compare|notes/i.test(part)),
    )
    .slice(-3)
    .map((item) => truncate(item, 120));

  return {
    threadId,
    rollingSummary,
    activeGoals,
    openLoops: assistantQuestions,
    lastUpdatedAt: Date.now(),
    summaryVersion: (previous?.summaryVersion ?? 0) + 1,
  };
};

export const extractTopicLabel = (text: string): string => {
  const words = normalizeWhitespace(text)
    .toLowerCase()
    .split(/[^a-z0-9\u0600-\u06ff]+/i)
    .filter((word) => word.length > 2 && !STOPWORDS.has(word));
  return truncate(words.slice(0, 5).join(" "), MAX_TOPIC_LABEL_LENGTH);
};

export const estimateThreadPayload = (messages: ReaderAiMessage[], summary?: ReaderAiThreadSummary | null): number => {
  const recent = getRecentConversationTurns(messages);
  const recentSize = recent.reduce((total, message) => total + readerAiMessageToText(message).length, 0);
  const summarySize =
    (summary?.rollingSummary.length ?? 0) +
    (summary?.activeGoals.join(" ").length ?? 0) +
    (summary?.openLoops.join(" ").length ?? 0);
  return recentSize + summarySize;
};

export const hasHighTopicDrift = (
  prompt: string,
  summary?: ReaderAiThreadSummary | null,
  latestSourceText?: string | null,
): boolean => {
  const topic = extractTopicLabel(prompt);
  if (!topic) return false;

  const haystack = `${summary?.rollingSummary ?? ""} ${latestSourceText ?? ""}`.toLowerCase();
  const topicWords = topic.split(/\s+/).filter(Boolean);
  const matches = topicWords.filter((word) => haystack.includes(word)).length;
  return topicWords.length > 0 && matches === 0;
};
